import {
  Sparkles, Users, Clock, Compass, Activity, MessageSquare, Quote, Languages,
  TrendingUp, AlertOctagon, Target, HelpCircle, Bug, Gauge,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";
import { TopicTimeline } from "@/components/JevBadges";

function fmtMin(seconds) {
  if (!seconds) return "0m";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
}

function StatTile({ label, value, sub, icon: Icon, tone = "lavender", testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-3xl bg-card border border-border p-5 shadow-card hover:-translate-y-0.5 hover:shadow-lift transition-all duration-200"
    >
      <div className="flex items-start justify-between">
        <div className="eyebrow">{label}</div>
        {Icon && (
          <div className={`tone-${tone} w-9 h-9 rounded-full grid place-items-center`}>
            <Icon className="w-4 h-4" strokeWidth={1.6} />
          </div>
        )}
      </div>
      <div className="mt-3 font-heading text-4xl leading-none">
        {value}
      </div>
      {sub && <div className="mt-2 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function HealthGauge({ score }) {
  const s = Math.max(0, Math.min(100, score || 0));
  const tone = s >= 75 ? "sage" : s >= 50 ? "apricot" : "blush";
  const inkColor = s >= 75 ? "hsl(var(--ink-success))" : s >= 50 ? "hsl(var(--ink-warning))" : "hsl(var(--ink-danger))";
  const barColor = s >= 75 ? "bg-emerald-500" : s >= 50 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div className="yl-surface p-6" data-testid="recap-health-score">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow">Meeting health score</div>
          <div className="mt-3 flex items-baseline gap-3">
            <div className="font-heading text-7xl leading-none" style={{ color: inkColor }}>
              {s}
            </div>
            <div className="font-heading text-2xl text-muted-foreground">/ 100</div>
          </div>
        </div>
        <div className={`tone-${tone} px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.16em] font-semibold`}>
          {s >= 75 ? "Healthy" : s >= 50 ? "Mixed" : "Needs attention"}
        </div>
      </div>
      <div className="mt-5 h-2 rounded-full bg-secondary overflow-hidden">
        <div className={`h-2 rounded-full ${barColor}`} style={{ width: `${s}%` }} />
      </div>
      <div className="mt-3 text-xs text-muted-foreground">
        Combines on-agenda time, participation balance &amp; decisions per minute.
      </div>
    </div>
  );
}

const SPEAKER_COLORS = ["#6C5FBC", "#8B7FD4", "#3D6FA8", "#3E7D5C", "#B06A2C", "#B04A66"];

function HealthBreakdown({ breakdown }) {
  const s = Math.max(0, Math.min(100, breakdown.overall || 0));
  const inkColor = s >= 75 ? "hsl(var(--ink-success))" : s >= 50 ? "hsl(var(--ink-warning))" : "hsl(var(--ink-danger))";
  const tone = s >= 75 ? "sage" : s >= 50 ? "apricot" : "blush";
  return (
    <div className="yl-surface p-6" data-testid="recap-health-score">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow flex items-center gap-1.5"><Gauge className="w-3.5 h-3.5" strokeWidth={1.8} /> Meeting health score</div>
          <div className="mt-3 flex items-baseline gap-3">
            <div className="font-heading text-7xl leading-none" style={{ color: inkColor }}>{s}</div>
            <div className="font-heading text-2xl text-muted-foreground">/ 100</div>
          </div>
        </div>
        <div className={`tone-${tone} px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.16em] font-semibold`}>
          {s >= 75 ? "Healthy" : s >= 50 ? "Mixed" : "Needs attention"}
        </div>
      </div>
      <div className="mt-6 space-y-3">
        {(breakdown.components || []).map((c) => {
          const cs = Math.max(0, Math.min(100, c.score || 0));
          const bar = cs >= 75 ? "bg-emerald-500" : cs >= 50 ? "bg-amber-500" : "bg-rose-500";
          return (
            <div key={c.key} data-testid={`health-component-${c.key}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium">{c.label} <span className="text-muted-foreground">· {c.weight}%</span></span>
                <span className="text-muted-foreground">{cs}</span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className={`h-1.5 rounded-full ${bar}`} style={{ width: `${cs}%` }} />
              </div>
              {c.tip && <div className="mt-1 text-[11px] text-[hsl(var(--ink-warning))]">{c.tip}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Q_TONE = { Answered: "sage", "Partially answered": "apricot", Open: "blush" };
const SEV_TONE = { Low: "powder", Medium: "apricot", High: "blush", Critical: "blush" };


export default function MeetingRecap({ meeting, tasksCount, decisionsCount }) {
  const a = meeting.analytics || {};
  const gateFailed = a.quality_gate_passed === false;
  const hasData = a.duration_seconds > 0 || (a.agenda_coverage && a.agenda_coverage.length > 0) || gateFailed;

  if (!hasData) {
    return (
      <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
        <Sparkles className="w-6 h-6 mx-auto mb-3" strokeWidth={1.5} />
        Analytics weren&apos;t computed for this meeting. Record + process a meeting to see the recap.
      </div>
    );
  }

  const agendaBars = (a.agenda_coverage || []).map((c, i) => ({
    name: c.item || `Item ${i + 1}`,
    pct: c.percentage || 0,
    isOff: (c.item || "").toLowerCase() === "off-agenda",
    highlights: c.highlights,
  }));

  const speakerBars = (a.speakers || []).map((s, i) => ({
    name: s.name || "Unknown",
    minutes: s.minutes || 0,
    pct: s.percentage || 0,
    color: SPEAKER_COLORS[i % SPEAKER_COLORS.length],
  }));

  return (
    <div className="space-y-8" data-testid="meeting-recap">
      {/* Header — pastel hero */}
      <div className="rounded-3xl tone-lavender p-7 shadow-card">
        <div className="eyebrow">Meeting recap</div>
        <h2 className="font-heading text-4xl mt-2 text-foreground tracking-tight">
          {meeting.title}
        </h2>
        {a.engagement_note && (
          <p className="text-sm text-foreground/85 mt-4 max-w-3xl leading-relaxed">
            {a.engagement_note}
          </p>
        )}
        {meeting.summary && (
          <p className="text-sm text-foreground/70 mt-3 max-w-3xl leading-relaxed border-l-2 border-primary/40 pl-4">
            {meeting.summary}
          </p>
        )}
      </div>

      {/* Top stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatTile
          label="Duration"
          value={a.duration_minutes ? `${a.duration_minutes}m` : fmtMin(a.duration_seconds || 0)}
          sub={a.words_per_minute ? `${a.words_per_minute} words/min` : null}
          icon={Clock}
          tone="lavender"
          testid="recap-duration"
        />
        <StatTile
          label="On agenda"
          value={gateFailed ? "—" : `${a.on_agenda_percentage || 0}%`}
          sub={gateFailed ? "Insufficient data" : (a.drift_incidents > 0 ? `${a.drift_incidents} drift incident${a.drift_incidents === 1 ? "" : "s"}` : "No drift detected")}
          icon={Target}
          tone={gateFailed ? "powder" : (a.on_agenda_percentage >= 75 ? "sage" : "apricot")}
          testid="recap-on-agenda"
        />
        <StatTile
          label="Speakers"
          value={a.speakers_count || 0}
          sub={a.speakers?.[0]?.name ? `${a.speakers[0].name} led (${a.speakers[0].percentage}%)` : null}
          icon={Users}
          tone="powder"
          testid="recap-speakers"
        />
        <StatTile
          label="Decisions"
          value={decisionsCount}
          icon={TrendingUp}
          tone="sage"
          testid="recap-decisions"
        />
        <StatTile
          label="Action items"
          value={tasksCount}
          icon={Activity}
          tone="apricot"
          testid="recap-actions"
        />
        <StatTile
          label="Words spoken"
          value={(a.word_count || 0).toLocaleString()}
          icon={MessageSquare}
          tone="blush"
          testid="recap-words"
        />
      </div>

      {gateFailed ? (
        <div className="rounded-3xl border border-rose-200 bg-rose-50/60 p-6" data-testid="recap-health-insufficient">
          <div className="eyebrow">Meeting health score</div>
          <div className="mt-3 font-heading text-3xl text-rose-700">Insufficient data</div>
          <div className="mt-2 text-xs text-rose-700/80">
            The transcript quality gate did not pass, so the Health Score and agenda-drift
            metrics are withheld to avoid misleading numbers. Re-transcribe from the Overview tab.
          </div>
        </div>
      ) : a.health_breakdown ? (
        <HealthBreakdown breakdown={a.health_breakdown} />
      ) : (
        <HealthGauge score={a.meeting_health_score} />
      )}

      {/* Questions + Issues */}
      {(a.questions?.length > 0 || a.issues?.length > 0) && (
        <div className="grid lg:grid-cols-2 gap-6">
          {a.questions?.length > 0 && (
            <section className="yl-surface p-6" data-testid="recap-questions">
              <div className="eyebrow mb-4 flex items-center gap-2"><HelpCircle className="w-3.5 h-3.5" strokeWidth={1.6} /> Questions raised</div>
              <ul className="space-y-3">
                {(a.questions || []).map((q, i) => (
                  <li key={i} className="text-sm flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-foreground/90">{q.question}</div>
                      {q.asked_by && <div className="text-xs text-muted-foreground mt-0.5">asked by {q.asked_by}</div>}
                    </div>
                    <span className={`tone-${Q_TONE[q.status] || "powder"} shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold`}>{q.status || "Open"}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {a.issues?.length > 0 && (
            <section className="yl-surface p-6" data-testid="recap-issues">
              <div className="eyebrow mb-4 flex items-center gap-2"><Bug className="w-3.5 h-3.5" strokeWidth={1.6} /> Issues reported</div>
              <ul className="space-y-3">
                {(a.issues || []).map((it, i) => (
                  <li key={i} className="text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-foreground/90">{it.description}</div>
                      <span className={`tone-${SEV_TONE[it.severity] || "powder"} shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold`}>{it.severity || "Medium"}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {it.module ? `${it.module} · ` : ""}{it.reported_by ? `reported by ${it.reported_by}` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {/* Agenda coverage chart */}
      {agendaBars.length > 0 && (
        <section className="rounded-3xl bg-card border border-border shadow-card overflow-hidden" data-testid="recap-agenda-coverage">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <div className="tone-lavender w-8 h-8 rounded-full grid place-items-center">
              <Compass className="w-4 h-4" strokeWidth={1.6} />
            </div>
            <div className="eyebrow">Agenda coverage</div>
          </div>
          <div className="p-6">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={agendaBars} layout="vertical" margin={{ left: 8, right: 24, top: 8, bottom: 8 }}>
                  <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11, fill: "#8B8699" }} stroke="#E8E4F0" />
                  <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12, fill: "#2A2438" }} stroke="#E8E4F0" />
                  <Tooltip
                    cursor={{ fill: "hsl(var(--secondary) / .4)" }}
                    formatter={(val) => [`${val}%`, "Share"]}
                    contentStyle={{ borderRadius: 14, borderColor: "#E8E4F0", boxShadow: "0 8px 24px rgba(42,36,56,0.08)" }}
                  />
                  <Bar dataKey="pct" radius={[0, 8, 8, 0]}>
                    {(agendaBars || []).map((b, i) => (
                      <Cell key={i} fill={b.isOff ? "#B04A66" : "#6C5FBC"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-5 space-y-2">
              {(agendaBars || []).map((b, i) => (
                <div key={i} className="text-xs flex items-start gap-3">
                  <span className={"shrink-0 inline-block w-2.5 h-2.5 mt-1.5 rounded-full " + (b.isOff ? "bg-[hsl(var(--ink-danger))]" : "bg-primary")} />
                  <div className="min-w-0">
                    <span className="font-semibold">{b.name}</span>
                    <span className="text-muted-foreground"> · {b.pct}%</span>
                    {b.highlights && <span className="text-muted-foreground"> — {b.highlights}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Speakers chart */}
      {speakerBars.length > 0 && (
        <section className="rounded-3xl bg-card border border-border shadow-card overflow-hidden" data-testid="recap-speakers-chart">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <div className="tone-powder w-8 h-8 rounded-full grid place-items-center">
              <Users className="w-4 h-4" strokeWidth={1.6} />
            </div>
            <div className="eyebrow">Speaking time</div>
          </div>
          <div className="p-6">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={speakerBars} margin={{ left: 8, right: 8, top: 8, bottom: 16 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#2A2438" }} stroke="#E8E4F0" />
                  <YAxis tickFormatter={(v) => `${v}m`} tick={{ fontSize: 11, fill: "#8B8699" }} stroke="#E8E4F0" />
                  <Tooltip
                    cursor={{ fill: "hsl(var(--secondary) / .4)" }}
                    formatter={(val, _name, p) => [`${val}m (${p?.payload?.pct ?? 0}%)`, "Time"]}
                    contentStyle={{ borderRadius: 14, borderColor: "#E8E4F0", boxShadow: "0 8px 24px rgba(42,36,56,0.08)" }}
                  />
                  <Bar dataKey="minutes" radius={[8, 8, 0, 0]}>
                    {(speakerBars || []).map((b, i) => (
                      <Cell key={i} fill={b.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      )}

      {/* Topic timeline ribbon (Jev) */}
      {a.topic_timeline?.length > 0 && <TopicTimeline timeline={a.topic_timeline} />}

      {/* Topics + Key quotes side by side */}
      <div className="grid lg:grid-cols-2 gap-6">
        {a.topics_discussed?.length > 0 && (
          <section className="yl-surface p-6" data-testid="recap-topics">
            <div className="eyebrow mb-4">Topics discussed</div>
            <div className="flex flex-wrap gap-2">
              {(a.topics_discussed || []).map((tx, i) => (
                <span
                  key={i}
                  className="tone-lavender rounded-full px-3.5 py-1.5 text-xs font-semibold"
                >
                  {tx}
                </span>
              ))}
            </div>
          </section>
        )}
        {a.key_quotes?.length > 0 && (
          <section className="yl-surface p-6" data-testid="recap-quotes">
            <div className="eyebrow mb-4 flex items-center gap-2">
              <Quote className="w-3.5 h-3.5" strokeWidth={1.6} /> Key quotes
            </div>
            <ul className="space-y-3">
              {(a.key_quotes || []).map((q, i) => (
                <li key={i} className="text-sm border-l-2 border-primary pl-4 italic text-foreground/85">
                  &ldquo;{q}&rdquo;
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {/* Footer line */}
      <div className="rounded-2xl bg-card border border-border px-5 py-3 text-xs text-muted-foreground flex items-center justify-between flex-wrap gap-3">
        {a.languages_detected?.length > 0 && (
          <div className="flex items-center gap-2">
            <Languages className="w-3.5 h-3.5" strokeWidth={1.6} />
            {a.languages_detected.join(", ")}
          </div>
        )}
        {!gateFailed && a.drift_incidents > 0 && (
          <div className="flex items-center gap-2 text-[hsl(var(--ink-warning))]">
            <AlertOctagon className="w-3.5 h-3.5" strokeWidth={1.6} />
            {a.drift_incidents} agenda-drift incident{a.drift_incidents === 1 ? "" : "s"}
          </div>
        )}
      </div>
    </div>
  );
}
